import * as React from 'react';
import { cn } from '@/shared/lib/cn';
import { Tooltip } from '@/shared/ui/Tooltip/Tooltip';
import styles from './IconButton.module.css';

export type IconButtonTone = 'neutral' | 'danger';
export type IconButtonSize = 'sm' | 'md' | 'lg';

export interface IconButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'> {
  label: string;
  tone?: IconButtonTone;
  size?: IconButtonSize;
  tooltip?: React.ReactNode;
  tooltipSide?: 'top' | 'right' | 'bottom' | 'left';
}

/**
 * Кнопка-действие с одной иконкой: правка, архив, удаление, «убрать из списка».
 *
 *     <IconButton label="Удалить клиента Ромашка" tone="danger" onClick={…}>
 *       <Trash2 size={16} aria-hidden />
 *     </IconButton>
 *
 * Тултип встроен, потому что иконка без подписи сама по себе непонятна: если
 * оборачивать вручную, половина мест остаётся без объяснения.
 *
 * Недоступное состояние — `aria-disabled`, а не нативный `disabled`: см.
 * комментарий в IconButton.module.css.
 *
 * @use кнопка без подписи, одна иконка — действия в строке списка и в шапке. `tone="danger"` для удаления и снятия связей.
 */
export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton(
    {
      label,
      tone = 'neutral',
      size = 'md',
      tooltip,
      tooltipSide,
      disabled,
      className,
      onClick,
      children,
      ...rest
    },
    ref,
  ) {
    const btn = (
      <button
        ref={ref}
        type="button"
        aria-label={label}
        aria-disabled={disabled || undefined}
        className={cn(
          styles.iconBtn,
          styles[`size-${size}`],
          styles[`tone-${tone}`],
          disabled && styles.disabled,
          className,
        )}
        onClick={(event) => {
          if (disabled) {
            event.preventDefault();
            return;
          }
          onClick?.(event);
        }}
        {...rest}
      >
        {children}
      </button>
    );

    return (
      <Tooltip content={tooltip ?? label} side={tooltipSide}>
        {btn}
      </Tooltip>
    );
  },
);
